import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  GlobalStyles,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";

import { useRecoilValue } from "recoil";
import { LoginUserAtom } from "../stores/LoginUserAtom";

import axios from "axios";
import ConstList from "../utils/ConstList";

type ReleaseNoteRow = {
  ID: number | string;
  Version: string;
  ChangeDay: string;
  Title: string;
  Summary: string;
  Features: string[];
  Improvements: string[];
  Fixes: string[];
  DataApi: string[];
  Notices: string[];
};

const colors = {
  bg: "#2D2C3A",
  panel: "#1F2530",
  panel2: "#252B3A",
  header: "#1F1D30",
  accent: "#ff8579",
  accentSoft: "rgba(255,133,121,0.12)",
  accentBorder: "rgba(255,133,121,0.36)",
  text: "#F5F7FA",
  textSoft: "rgba(232,238,245,0.78)",
  textMuted: "#9FB0C0",
  border: "rgba(217,217,217,0.12)",
};

const toText = (value: unknown) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const toList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => toText(item).trim()).filter(Boolean);
  }

  return toText(value)
    .split(/\r?\n/)
    .map((item) => item.replace(/^[-・\s]+/, "").trim())
    .filter(Boolean);
};

const formatDate = (value: unknown) => {
  const text = toText(value);

  if (!text) return "";

  const date = new Date(text);

  if (Number.isNaN(date.getTime())) return text;

  return date.toLocaleDateString("ja-JP");
};

const CategoryList = ({
  title,
  items,
}: {
  title: string;
  items: string[];
}) => {
  if (!items || items.length === 0) return null;

  return (
    <Box sx={{ mt: 1.4 }}>
      <Typography
        sx={{
          color: colors.accent,
          fontSize: 13,
          fontWeight: 900,
          mb: 0.75,
        }}
      >
        {title}
      </Typography>

      <Stack spacing={0.6}>
        {items.map((item, index) => (
          <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: colors.accent,
                mt: 0.85,
                flexShrink: 0,
              }}
            />

            <Typography
              sx={{
                color: colors.textSoft,
                fontSize: 13,
                lineHeight: 1.7,
                whiteSpace: "pre-line",
                wordBreak: "break-word",
              }}
            >
              {item}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const normalizeReleaseNotes = (rawList: any[]): ReleaseNoteRow[] => {
  return rawList.map((value: any, index: number) => {
    const version = toText(value.version ?? value.Version ?? index + 1);

    return {
      ID: value.release_note_id ?? value.version_id ?? value.ID ?? index,
      Version: version,

      ChangeDay: formatDate(
        value.release_date ?? value.changes_date ?? value.ChangeDay
      ),

      Title: toText(value.title) || `Ver.${version}`,
      Summary:
        toText(value.summary) ||
        toText(value.description) ||
        "営業管理システムの変更内容です。",

      Features: [
        ...toList(value.features),
        ...toList(value.new_features),
        ...toList(value.Change),
      ],

      Improvements: [
        ...toList(value.improvements),
        ...toList(value.improvement),
        ...toList(value.changes),
      ],

      Fixes: [
        ...toList(value.fixes),
        ...toList(value.bug_fixes),
        ...toList(value.category),
        ...toList(value.Contents),
      ],

      DataApi: [
        ...toList(value.data_api),
        ...toList(value.dataApi),
        ...toList(value.api_changes),
        ...toList(value.database_changes),
      ],

      Notices: [
        ...toList(value.notices),
        ...toList(value.notes),
        ...toList(value.attention),
      ],
    };
  });
};

const ReleaseNotes: React.FC = () => {
  const loginUserData = useRecoilValue(LoginUserAtom);

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [releaseNoteData, setReleaseNoteData] = useState<ReleaseNoteRow[]>([]);

  const fetchData = async () => {
    setLoading(true);
    setErrorMessage(null);

    const postData = {
      flag: "GetSalesReleaseNotes",
      access_token: loginUserData.accessToken,
      algorithm: loginUserData.algorithm,
    };

    try {
      const response = await axios.post(
        ConstList.getApiUrl() + "SalesReleaseNotes.php",
        postData,
        ConstList.AXIOS_CONFIG
      );

      if (response.data?.data === false) {
        setErrorMessage(
          response.data?.message ?? "リリースノートの取得に失敗しました。"
        );

        if (response.data?.reDirect) {
          await ConstList.SLEEP(5000);
          window.location.href = response.data.reDirect;
        }

        return;
      }

      const payload = response.data?.data ?? {};

      const rawList =
        payload.release_notes ??
        payload.releaseNotes ??
        payload.sales_release_notes ??
        payload.ReleaseData ??
        payload.ReleaceData ??
        [];

      setReleaseNoteData(
        normalizeReleaseNotes(Array.isArray(rawList) ? rawList : [])
      );
    } catch (error) {
      console.error("リリースノート取得エラー:", error);
      setErrorMessage("リリースノートの取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loginUserData?.accessToken || !loginUserData?.algorithm) return;
    fetchData();
  }, [
    loginUserData?.accessToken,
    loginUserData?.algorithm,
  ]);

  return (
    <>
      <GlobalStyles
        styles={{
          html: { overflowY: "hidden" },
          body: { overflowY: "hidden" },
          "#root": { overflowY: "hidden" },
        }}
      />

      <Box
        sx={{
          height: {
            xs: "calc(100dvh - 96px)",
            md: "calc(100dvh - 150px)",
          },
          maxHeight: {
            xs: "calc(100dvh - 96px)",
            md: "calc(100dvh - 150px)",
          },
          minHeight: 0,
          overflow: "hidden",
          backgroundColor: colors.bg,
          color: colors.text,
          p: { xs: 1.25, md: 2 },
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 980,
            mx: "auto",
            height: "100%",
            minHeight: 0,
            p: { xs: 1.5, md: 2 },
            borderRadius: 3,
            backgroundColor: colors.panel,
            border: `1px solid ${colors.border}`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box sx={{ flexShrink: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CampaignRoundedIcon sx={{ color: colors.accent }} />

              <Typography
                sx={{
                  color: colors.text,
                  fontSize: { xs: 20, md: 24 },
                  fontWeight: 900,
                  letterSpacing: "0.03em",
                }}
              >
                リリースノート
              </Typography>
            </Stack>

            <Typography
              sx={{
                color: colors.textMuted,
                fontSize: 13,
                mt: 0.75,
              }}
            >
              営業管理システムの変更履歴・アップデート情報を確認できます。
            </Typography>
          </Box>

          <Divider sx={{ borderColor: colors.border, my: 1.5, flexShrink: 0 }} />

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              pr: { xs: 0, md: 0.5 },
            }}
          >
            <Stack spacing={1}>
              {errorMessage && (
                <Alert
                  severity="error"
                  sx={{
                    backgroundColor: colors.accentSoft,
                    color: colors.text,
                    border: `1px solid ${colors.accentBorder}`,
                    "& .MuiAlert-icon": {
                      color: colors.accent,
                    },
                  }}
                >
                  {errorMessage}
                </Alert>
              )}

              {loading ? (
                <Box
                  sx={{
                    minHeight: 180,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: colors.textMuted,
                  }}
                >
                  <CircularProgress sx={{ color: colors.accent }} />
                  <Typography sx={{ ml: 1.5 }}>読み込み中...</Typography>
                </Box>
              ) : releaseNoteData.length === 0 ? (
                <Box
                  sx={{
                    p: 3,
                    textAlign: "center",
                    color: colors.textMuted,
                    border: `1px dashed ${colors.border}`,
                    borderRadius: 2,
                  }}
                >
                  表示できるリリースノートがありません。
                </Box>
              ) : (
                releaseNoteData.map((note, index) => (
                  <Accordion
                    key={`${note.ID}-${note.Version}`}
                    defaultExpanded={index === 0}
                    disableGutters
                    sx={{
                      backgroundColor: colors.panel2,
                      color: colors.text,
                      border: `1px solid ${colors.border}`,
                      borderRadius: "12px !important",
                      overflow: "hidden",
                      "&::before": {
                        display: "none",
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: colors.textMuted }} />}
                      sx={{
                        minHeight: 56,
                        "& .MuiAccordionSummary-content": {
                          my: 0.9,
                        },
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        sx={{ width: "100%" }}
                      >
                        <Chip
                          size="small"
                          label={`Ver.${note.Version}`}
                          sx={{
                            color: "#111827",
                            backgroundColor: colors.accent,
                            fontWeight: 900,
                          }}
                        />

                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 900 }}>
                            {note.Title}
                          </Typography>

                          <Typography
                            sx={{
                              color: colors.textMuted,
                              fontSize: 12.5,
                            }}
                          >
                            {note.ChangeDay || "-"}　{note.Summary}
                          </Typography>
                        </Box>
                      </Stack>
                    </AccordionSummary>

                    <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
                      <CategoryList title="機能追加" items={note.Features} />
                      <CategoryList title="改善" items={note.Improvements} />
                      <CategoryList title="修正" items={note.Fixes} />
                      <CategoryList title="データ / API" items={note.DataApi} />
                      <CategoryList title="注意事項" items={note.Notices} />
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </Stack>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default ReleaseNotes;
